// ─────────────────────────────────────────────────────────────────────────────
// Synthetic fleet registry.
//
// 100 units, DX-01 … DX-100. Three units carry demo data; the rest are listed
// but disabled, so the selector shows realistic fleet scale without implying
// data we don't have.
//
// ANONYMISATION: unit IDs are sequential and fictional. Do not map them to
// real running numbers.
// ─────────────────────────────────────────────────────────────────────────────

export type UnitStatus = 'ready' | 'no-data';

/**
 * Per-unit character. Everything the generators need to make one unit read
 * differently from the next, without duplicating datasets.
 *
 * The three ready units tell contrasting stories on purpose:
 *   DX-04  baseline — one door degrading, moderate misuse
 *   DX-09  high mileage, well behaved — wear-driven, very little misuse
 *   DX-12  busy urban duty — newer hardware, damage driven by misuse
 */
export interface UnitProfile {
  /** Offsets every deterministic generator so units differ. */
  seedOffset: number;
  /** Boarding intensity multiplier. */
  loadScale: number;
  /** Lifetime cycle-count multiplier. */
  cyclesScale: number;
  /** Forced-entry / misuse frequency multiplier. */
  misuseScale: number;
  /** Doors with a known condition, and their health score. */
  watchDoors: Record<string, number>;
  /** One-line characterisation, shown in the fleet menu. */
  note: string;
  /** Duty description for page headers. */
  duty: string;
}

export const UNIT_PROFILES: Record<string, UnitProfile> = {
  'DX-04': {
    seedOffset: 0,
    loadScale: 1.0,
    cyclesScale: 1.0,
    misuseScale: 1.0,
    watchDoors: { 'C2L': 61, 'B1R': 74 },
    note: 'Baseline · one door degrading',
    duty: 'Regional service',
  },
  'DX-09': {
    seedOffset: 3301,
    loadScale: 0.85,
    cyclesScale: 1.45,
    misuseScale: 0.35,
    watchDoors: { 'A2R': 68, 'D2L': 77 },
    note: 'High mileage · wear-driven',
    duty: 'Long-distance service',
  },
  'DX-12': {
    seedOffset: 6602,
    loadScale: 1.25,
    cyclesScale: 0.78,
    misuseScale: 2.2,
    watchDoors: { 'D1L': 52, 'B2R': 66, 'C1L': 71 },
    note: 'Busy urban duty · misuse-driven',
    duty: 'Urban service',
  },
};

/** Units with demo data wired up. */
export const READY_UNIT_IDS = Object.keys(UNIT_PROFILES);

/** Default selection. */
export const AVAILABLE_UNIT_ID = 'DX-04';

export interface FleetUnit {
  id: string;
  unitClass: string;
  depot: string;
  status: UnitStatus;
  note?: string;
}

const DEPOTS = ['North', 'Central', 'South', 'West'];

export const FLEET: FleetUnit[] = Array.from({ length: 100 }, (_, i) => {
  const n = i + 1;
  const id = `DX-${String(n).padStart(2, '0')}`;
  const ready = READY_UNIT_IDS.includes(id);
  return {
    id,
    unitClass: '4-car EMU',
    depot: DEPOTS[i % DEPOTS.length],
    status: ready ? 'ready' : 'no-data',
    note: ready ? UNIT_PROFILES[id].note : undefined,
  };
});

export const findUnit = (id: string) => FLEET.find(u => u.id === id);

/** Profile for a unit, falling back to the baseline. */
export const profileFor = (id: string): UnitProfile =>
  UNIT_PROFILES[id] ?? UNIT_PROFILES[AVAILABLE_UNIT_ID];

/** Caption for page headers, e.g. '4-car EMU · unit DX-04'. */
export const unitCaption = (id: string) => {
  const u = findUnit(id);
  return u ? `${u.unitClass} · ${u.id}` : id;
};

// ── Reporting date ───────────────────────────────────────────────────────────
// Shown at the top of the fleet menu so the analysis date is explicit rather
// than implied. Reads the real clock, so it stays correct at a live demo.

export const reportingDate = (locale = 'de-DE') =>
  new Date().toLocaleDateString(locale, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

export const reportingTime = (locale = 'de-DE') =>
  new Date().toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

/** Standard disclaimer for prototype footers. */
export const SYNTHETIC_NOTE =
  'Synthetic fleet — unit IDs, stop names, timings and identifiers are fictional';
